import {
  AppUpdateProcessEnum,
  DevicePlatformsEnum,
  LiveUpdateStatusesEnum,
} from "@/enums/appSettings";
import { MimeTypesEnum } from "@/enums/global";
import {
  buildLiveBundleId,
  isAppVersionOutdated,
  parseLiveBundleId,
  updateLoaderText,
} from "@/methods/appUtils";
import { openExternalLink } from "@/methods/externalLink";
import { downloadFileFromUrl, getAzureUrl } from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { AppUpdateStore } from "@/types/global";
import {
  AppUpdate,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
} from "@capawesome/capacitor-app-update";
import type { DownloadBundleProgressEvent } from "@capawesome/capacitor-live-update";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { defineStore, storeToRefs } from "pinia";
import { toRef } from "vue";

function getInitialAppUpdateStore(): AppUpdateStore {
  return {
    AppUpdateProcess: AppUpdateProcessEnum.NONE,
    AppUpdateListener: null,
    InAppUpdateStatus: FlexibleUpdateInstallStatus.UNKNOWN,
    LiveUpdateStatus: LiveUpdateStatusesEnum.IDLE,
    UpdateProgress: 0,
    ErrorMessage: null,
    CurrentBundleId: null,
  };
}

export const useAppUpdateStore = defineStore("appUpdateStore", {
  state: (): AppUpdateStore => getInitialAppUpdateStore(),
  getters: {
    IsLiveUpdating(): boolean {
      return this.AppUpdateProcess === AppUpdateProcessEnum.LIVE_UPDATE;
    },
    IsInAppUpdating(): boolean {
      return this.AppUpdateProcess === AppUpdateProcessEnum.IN_APP_UPDATE;
    },
    IsApkUpdating(): boolean {
      return this.AppUpdateProcess === AppUpdateProcessEnum.APK_UPDATE;
    },
    IsUpdating(): boolean {
      return (
        (this.IsLiveUpdating && this.LiveUpdateStatus === LiveUpdateStatusesEnum.DOWNLOADING) ||
        ((this.IsInAppUpdating || this.IsApkUpdating) &&
          (this.InAppUpdateStatus === FlexibleUpdateInstallStatus.PENDING ||
            this.InAppUpdateStatus === FlexibleUpdateInstallStatus.DOWNLOADING))
      );
    },
    IsUpdateReady(): boolean {
      return (
        (this.IsLiveUpdating && this.LiveUpdateStatus === LiveUpdateStatusesEnum.DOWNLOADED) ||
        ((this.IsInAppUpdating || this.IsApkUpdating) &&
          this.InAppUpdateStatus === FlexibleUpdateInstallStatus.DOWNLOADED)
      );
    },
    HasUpdateError(): boolean {
      return (
        (this.IsLiveUpdating && this.LiveUpdateStatus === LiveUpdateStatusesEnum.ERROR) ||
        ((this.IsInAppUpdating || this.IsApkUpdating) &&
          (this.InAppUpdateStatus === FlexibleUpdateInstallStatus.FAILED ||
            this.InAppUpdateStatus === FlexibleUpdateInstallStatus.CANCELED))
      );
    },
    IsLiveUpdateApplying(): boolean {
      return this.IsLiveUpdating && this.LiveUpdateStatus === LiveUpdateStatusesEnum.APPLYING;
    },
    UpdateMessage(): string {
      const appSettingsStore = useAppSettingsStore();
      const { LatestAppVersion, IsForcedAppUpdate } = storeToRefs(appSettingsStore);
      if (LatestAppVersion.value && LatestAppVersion.value.UpdateMessage) {
        return LatestAppVersion.value.UpdateMessage;
      }
      return `A new version of the app is available. ${
        IsForcedAppUpdate.value
          ? "Please update to the latest version to continue."
          : "It is recommended that you update the app as soon as possible."
      }`;
    },
    UpdateErrorMessage(): string {
      return this.IsLiveUpdating
        ? "App live update failed. Please check your internet connection and try again."
        : this.IsInAppUpdating && this.InAppUpdateStatus === FlexibleUpdateInstallStatus.CANCELED
          ? "App update was cancelled. You may retry the update."
          : "App update failed. Please try again or contact support.";
    },
  },
  actions: {
    async initializeAppUpdate() {
      const appSettingsStore = useAppSettingsStore();
      const { IsNativePlatform, DevicePlatform, IsLiveUpdateAvailable } =
        storeToRefs(appSettingsStore);
      this.AppUpdateProcess = AppUpdateProcessEnum.NONE;
      this.UpdateProgress = 0;
      if (!IsNativePlatform.value) {
        return;
      }
      if (IsLiveUpdateAvailable.value) {
        await this.initializeAppLiveUpdate();
      } else if (DevicePlatform.value === DevicePlatformsEnum.ANDROID) {
        await this.initializeInAppUpdate();
      }
    },
    async validateCurrentAppBundle(): Promise<void> {
      const appSettingsStore = useAppSettingsStore();
      const { IsNativePlatform, ClientNativeVersion } = storeToRefs(appSettingsStore);
      if (!IsNativePlatform.value) {
        return;
      }
      try {
        const result = await LiveUpdate.ready();
        this.CurrentBundleId = result.currentBundleId;
        if (result.rollback) {
          console.warn("App live update rollback detected. Reverted to previous bundle.");
        }
        if (this.CurrentBundleId && ClientNativeVersion.value !== null) {
          const { minNativeVersion } = parseLiveBundleId(this.CurrentBundleId);
          if (
            minNativeVersion !== null &&
            isAppVersionOutdated(ClientNativeVersion.value, minNativeVersion)
          ) {
            console.warn(
              `App live update: bundle requires native >= ${minNativeVersion} but installed native is ${ClientNativeVersion.value}. Resetting to built-in bundle.`,
            );
            await this.resetToDefaultBundle();
            return;
          }
        }
      } catch (error: any) {
        console.error("App live update: ready() call failed:", error.message);
      }
    },
    async initializeAppLiveUpdate(): Promise<void> {
      const appSettingsStore = useAppSettingsStore();
      const { IsNativePlatform } = storeToRefs(appSettingsStore);
      if (!IsNativePlatform.value) {
        return;
      }
      try {
        if (!this.CurrentBundleId) {
          const currentBundle = await LiveUpdate.getCurrentBundle();
          this.CurrentBundleId = currentBundle.bundleId;
        }
        this.AppUpdateProcess = AppUpdateProcessEnum.LIVE_UPDATE;
      } catch (error: any) {
        console.error("App live update initialization error:", error.message);
      }
    },
    async initializeInAppUpdate(): Promise<void> {
      try {
        const info = await AppUpdate.getAppUpdateInfo();
        if (!info.flexibleUpdateAllowed) {
          return;
        }
        this.AppUpdateListener = await AppUpdate.addListener(
          "onFlexibleUpdateStateChange",
          (state) => {
            this.InAppUpdateStatus = state.installStatus;
            if (
              state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADING &&
              state.totalBytesToDownload
            ) {
              this.UpdateProgress = Math.round(
                ((state.bytesDownloaded ?? 0) / state.totalBytesToDownload) * 100,
              );
            }
          },
        );
        this.AppUpdateProcess = AppUpdateProcessEnum.IN_APP_UPDATE;
      } catch {
        const appSettingsStore = useAppSettingsStore();
        const { LatestAppVersion } = storeToRefs(appSettingsStore);
        if (LatestAppVersion.value?.AppApkUrl) {
          this.AppUpdateProcess = AppUpdateProcessEnum.APK_UPDATE;
        }
        return;
      }
    },
    async startAppUpdate() {
      if (this.IsLiveUpdating) {
        await this.startAppLiveUpdate();
      } else if (this.IsInAppUpdating) {
        await this.startInAppUpdate();
      } else if (this.IsApkUpdating) {
        this.startApkUpdate();
      } else {
        await this.startNormalAppUpdate();
      }
    },
    async autoAppLiveUpdate(): Promise<void> {
      updateLoaderText("Downloading latest app...");
      await this.startAppLiveUpdate();
      updateLoaderText("Restarting app...");
      await this.onRestartApp();
    },
    async startAppLiveUpdate() {
      const appSettingsStore = useAppSettingsStore();
      const { IsNativePlatform, LatestAppVersion } = storeToRefs(appSettingsStore);
      if (
        !(
          IsNativePlatform.value &&
          LatestAppVersion.value &&
          LatestAppVersion.value.AppBundleUrl &&
          this.LiveUpdateStatus === LiveUpdateStatusesEnum.IDLE
        )
      ) {
        return;
      }
      const stampedBundleId = buildLiveBundleId(
        LatestAppVersion.value.AppVersion,
        LatestAppVersion.value.MinNativeVersion,
      );
      try {
        this.AppUpdateListener = await LiveUpdate.addListener(
          "downloadBundleProgress",
          (event: DownloadBundleProgressEvent) => {
            this.UpdateProgress = Math.round(event.progress * 100);
          },
        );
        this.LiveUpdateStatus = LiveUpdateStatusesEnum.DOWNLOADING;
        this.UpdateProgress = 0;
        this.ErrorMessage = null;
        await LiveUpdate.downloadBundle({
          bundleId: stampedBundleId,
          url: getAzureUrl(LatestAppVersion.value.AppBundleUrl),
          artifactType: "zip",
        });
        await LiveUpdate.setNextBundle({ bundleId: stampedBundleId });
        this.UpdateProgress = 100;
        this.LiveUpdateStatus = LiveUpdateStatusesEnum.DOWNLOADED;
      } catch (error: any) {
        console.error("App live update download error:", error.message);
        this.LiveUpdateStatus = LiveUpdateStatusesEnum.ERROR;
        this.ErrorMessage = error.message || "Failed to download update. Please try again later.";
      }
    },
    async startInAppUpdate() {
      if (this.IsInAppUpdating) {
        try {
          const startResult = await AppUpdate.startFlexibleUpdate();
          if (startResult.code !== AppUpdateResultCode.OK) {
            await this.cleanupAppUpdate();
            const updateResult = await AppUpdate.performImmediateUpdate();
            if (updateResult.code === AppUpdateResultCode.OK) {
              return;
            }
          }
        } catch {
          console.debug("Immediate update failed, redirecting to app store...");
        }
      }
      // If immediate update fails (e.g. due to user cancellation or error), fallback
      // to normal update flow
      await this.startNormalAppUpdate();
    },
    async startApkUpdate() {
      const appSettingsStore = useAppSettingsStore();
      const { LatestAppVersion } = storeToRefs(appSettingsStore);
      if (LatestAppVersion.value?.AppApkUrl) {
        const apkUrl = getAzureUrl(LatestAppVersion.value.AppApkUrl);
        const fileName = apkUrl.substring(apkUrl.lastIndexOf("/") + 1);
        this.InAppUpdateStatus = FlexibleUpdateInstallStatus.DOWNLOADING;
        const isSuccess = await downloadFileFromUrl(
          apkUrl,
          MimeTypesEnum.APK,
          fileName,
          false,
          toRef(this, "UpdateProgress"),
        );
        if (!isSuccess) {
          this.InAppUpdateStatus = FlexibleUpdateInstallStatus.FAILED;
          return;
        }
        this.InAppUpdateStatus = FlexibleUpdateInstallStatus.DOWNLOADED;
      }
    },
    async startNormalAppUpdate() {
      const appSettingsStore = useAppSettingsStore();
      const { IsNativePlatform, DevicePlatform, CompanyProfile } = storeToRefs(appSettingsStore);
      if (IsNativePlatform.value) {
        const storeUrl =
          DevicePlatform.value === DevicePlatformsEnum.IOS
            ? CompanyProfile.value.AppStores.iOS
            : CompanyProfile.value.AppStores.Android;
        // The store listing belongs to the OS store app, so it must not open in a Custom Tab.
        await openExternalLink(storeUrl, { useSystemBrowser: true });
      } else {
        window.location.reload();
      }
    },
    async onRestartApp() {
      if (this.IsLiveUpdating) {
        await this.applyLiveUpdate();
      } else if (this.IsInAppUpdating) {
        await AppUpdate.completeFlexibleUpdate();
      }
    },
    async applyLiveUpdate(): Promise<void> {
      const appSettingsStore = useAppSettingsStore();
      const { IsNativePlatform } = storeToRefs(appSettingsStore);
      if (!IsNativePlatform.value) {
        return;
      }
      try {
        this.LiveUpdateStatus = LiveUpdateStatusesEnum.APPLYING;
        await LiveUpdate.reload();
      } catch (error: any) {
        console.error("App live update reload error:", error.message);
        this.LiveUpdateStatus = LiveUpdateStatusesEnum.ERROR;
        this.ErrorMessage = "Failed to apply update. Please restart the app manually.";
      }
    },
    async resetToDefaultBundle(): Promise<void> {
      const appSettingsStore = useAppSettingsStore();
      const { IsNativePlatform } = storeToRefs(appSettingsStore);
      if (!IsNativePlatform.value) {
        return;
      }
      try {
        await LiveUpdate.reset();
        await LiveUpdate.reload();
      } catch (error: any) {
        console.error("App live update reset error:", error.message);
      }
    },
    async retryAppUpdate() {
      await this.cleanupAppUpdate();
      await this.initializeAppUpdate();
      await this.startAppUpdate();
    },
    async cleanupAppUpdate(): Promise<void> {
      if (this.IsLiveUpdating) {
        this.LiveUpdateStatus = LiveUpdateStatusesEnum.IDLE;
        const currentBundle = await LiveUpdate.getCurrentBundle();
        const allBundles = await LiveUpdate.getDownloadedBundles();
        for (const bundleId of allBundles.bundleIds) {
          if (bundleId !== currentBundle.bundleId) {
            await LiveUpdate.deleteBundle({ bundleId });
          }
        }
      } else if (this.IsInAppUpdating) {
        this.InAppUpdateStatus = FlexibleUpdateInstallStatus.UNKNOWN;
      }
      if (this.AppUpdateListener) {
        await this.AppUpdateListener.remove();
        this.AppUpdateListener = null;
      }
    },
  },
});
